/**
 * Profile priority widgets (b13d S3).
 *
 * Conditionally renders the trainer-style event radar, driven by the Nex
 * questionnaire priorities (single source of truth in lib/userPriorities):
 * it reads the existing live events feed (/api/events/live) and surfaces the
 * current + next matching event. Pro trainers get a friends tease (gated); free
 * trainers see the upsell.
 *
 * Nothing renders until the questionnaire is completed, so a brand-new trainer
 * is funnelled through ProfilePriorityRadar's "complete questionnaire" CTA
 * instead of seeing an empty style block.
 *
 * Also exports <PvpSeasonPanel> — the season + league tracker, which used to be
 * a questionnaire-gated child here and is now mounted by Profile on its own,
 * under the raid-boss panel. See its own doc comment below.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { leagueFocusLabel, type CollectionFocus } from "@/lib/userPriorities";
import { Swords, Shield, Check, ChevronRight, Lock, Users, Star } from "lucide-react";
import { EventRemindButton } from "@/components/EventRemindButton";
import { HeroCard, HeroCardMedia } from "@/components/ui/HeroCard";
import { InfoBlock } from "@/components/InfoBlock";
import { EvoLinesToggle } from "@/components/EvoLinesToggle";
import { useEvoLines } from "@/lib/evoLinesPref";
import { expandSpawnsToLines } from "@/lib/eventEvoLines";
import { CaughtSprite } from "@/components/CaughtSprite";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import {
  VariantBadge,
  isDexVariantKey,
  DEX_VARIANT_META,
  type DexVariantKey,
} from "@/components/dexVariantVisuals";
import { speciesName } from "@/data/speciesNames";
import { bindDexSyncApiFetch, getDexEntryState } from "@/lib/dexStateStore";
import { apiRequestRaw } from "@/lib/queryClient";
import {
  CatchUpTile,
  GAP_TILE_ROW_CLASS,
  GAP_TILE_CELL_STYLE,
} from "@/components/EventCatchUp";
import { useGapCommit } from "@/hooks/useGapCommit";
import {
  raidDexGaps,
  isMaxBattleEventCategory,
  sortGapsByVariant,
  type RaidDexVariant,
  type RaidDexGap,
} from "@/lib/raidDexCrossRef";
import {
  radarCategoriesFor,
  radarTitleFor,
  eventMatchesRadar,
  bestFriendTrades,
  pickRandomTrades,
  type FriendTrade,
  type FriendGapVariant, ALL_RADAR_CATEGORIES } from "@/lib/eventRadar";
import { useFriendDexes } from "@/hooks/useFriendDexes";
import { armCalendarEvent } from "@/lib/calendarDayScroll";
import {
  FRIEND_TRADES_EVENT,
  isFriendTradesEnabled,
  shouldShowFriendTrades,
} from "@/lib/friendTradesPref";
import { LAUNCH_SEED } from "@/lib/launchSeed";
import { isVariantAvailable } from "@/lib/variantAvailability";
import { isEventFormAvailable } from "@/lib/eventFormAvailability";
import { eventHasLinkedSpecies } from "@/lib/eventDexGaps";
import { eventsLiveKey } from "@/lib/eventsLiveKey";
import { pickRadarEvents } from "@/lib/radarPick";
import { resolvePvpSeason } from "@/lib/pvpSeasonProgress";
import { BonusBadges } from "@/components/BonusBadges";
import { mergedBonuses } from "@/lib/eventBonuses";

interface LiveEventApi {
  /** One-place event (worker lib/eventGeo): the standing countdown choice covers it only for an attendee. */
  geoLocked?: boolean;
  id: string;
  name: string;
  category: string;
  rawType: string;
  start: string;
  end: string;
  image?: string;
  // The /api/events/live feed serialises BoostedSpecies, which carries the
  // resolved speciesId + shinyBoost (not just a display name) — see
  // cloudflare/src/lib/events.ts. b15b U4 needs the id to cross-reference the
  // trainer's dex.
  spawns?: Array<{
    name?: string;
    speciesId?: number;
    shinyBoost?: boolean;
    // Present only on admin-linked events; drives the exact variants checked.
    variants?: RaidDexVariant[];
    // Form-scoped links (a costume / regional / Armored form): the worker
    // emits them and raidDexGaps reads them; typed here since 2026-09-07.
    forms?: Array<{ formId: string; variants?: RaidDexVariant[] }>;
    // Auto-detected form (worker feedFormSuffix, 2026-09-08): "armored", "galar".
    formSuffix?: string;
  }>;
  // Bonus lines the feed states ("2× Catch Candy") — see LiveEvent.bonuses in
  // cloudflare/src/lib/events.ts. Rendered as pills under each radar row.
  bonuses?: string[];
  /** Admin radar switch (migration 0085): pinned in, or excluded from, the radar. */
  radar?: "pin" | "exclude";
}
interface LiveEventsResponse {
  active: LiveEventApi[];
  upcoming: LiveEventApi[];
}

export function ProfilePriorityWidgets() {
  const { userPriorities } = useSession();
  const completed = userPriorities.questionnaireCompletedAt !== null;

  // The radar's event categories come from the trainer's play styles. This used
  // to be `if (!playsRaids && !playsPvp) return null`, which meant a collector,
  // trader or casual trainer saw NOTHING — the questionnaire asked what they
  // cared about and the profile then ignored three of the five answers.
  //
  // FOR EVERYONE (2026-09-04 owner direction): the questionnaire no longer
  // gates the block. Before it is answered, or when the styles pick nothing
  // (a PvP-only trainer), the radar watches EVERY category as a Catch radar;
  // answering it only narrows the lens.
  const { primaryStyle, secondaryStyle } = userPriorities;
  const styled = completed ? radarCategoriesFor(primaryStyle, secondaryStyle) : new Set<string>();
  const categories = styled.size > 0 ? styled : ALL_RADAR_CATEGORIES;

  // The PvP season block used to render here behind a `playsPvp` check, which is
  // why it vanished for anyone whose questionnaire styles aren't PvP. It is now
  // <PvpSeasonPanel>, mounted unconditionally by Profile directly under the
  // "Raids right now" panel (2026-08-07, user direction).
  return (
    <EventRadarBlock
      categories={categories}
      title={styled.size > 0 ? radarTitleFor(primaryStyle, secondaryStyle) : "Catch radar"}
    />
  );
}

/**
 * Whether the "Friends are looking for" box belongs on the event cards at
 * all (owner direction 2026-09-04): the Settings switch must be on and the
 * trainer must have at least one friend. Reads the same /api/friends query
 * the strip's own hook uses, so it costs no extra request.
 */
function useShowFriendTrades(): boolean {
  const [enabled, setEnabled] = useState(() => isFriendTradesEnabled());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => setEnabled(isFriendTradesEnabled());
    window.addEventListener(FRIEND_TRADES_EVENT, refresh);
    return () => window.removeEventListener(FRIEND_TRADES_EVENT, refresh);
  }, []);
  const friendsQ = useQuery<{ friends?: Array<{ friend?: { id?: number } | null }> }>({
    queryKey: ["/api/friends"],
    staleTime: 60_000,
    enabled,
  });
  const friendCount = (friendsQ.data?.friends ?? []).filter((r) => r.friend?.id != null).length;
  return shouldShowFriendTrades({
    enabled,
    friendCount,
    friendsLoaded: friendsQ.isSuccess || friendsQ.isError,
  });
}

function EventRadarBlock({
  categories,
  title,
}: {
  categories: ReadonlySet<string>;
  title: string;
}) {
  const showFriendTrades = useShowFriendTrades();
  const [, setLocation] = useLocation();
  const { tier, openPaywall, userPriorities } = useSession();
  const isPro = hasProAccess(tier);

  const liveQ = useQuery<LiveEventsResponse>({ queryKey: [eventsLiveKey()] });
  // Evo (2026-10-20): whole evolution lines on the gap strips, device-wide.
  const [evoLines] = useEvoLines();

  const raids = useMemo(() => {
    // 1.8.7: drop stale/past events using the SAME shared recency rule the
    // Calendar "Your plan" uses (END time, floating PoGO wall-clock), so Raid
    // Radar can never show an event as "NOW" that the plan already treats as
    // past. An event whose END is in the past is filtered before we pick the
    // current/next raid.
    // ONE rule, shared with the events admin page's radar preview
    // (lib/radarPick, 2026-09-05): the trainer's categories, minus admin
    // excludes, plus admin pins; "now" by the DEVICE clock (the server's
    // bucket lags a fest that opened at 10 a.m. local, and a month-long
    // research is background); "next" the soonest still to start.
    const { current, next } = pickRadarEvents(liveQ.data?.active ?? [], liveQ.data?.upcoming ?? [], categories);
    return { current, next };
  }, [liveQ.data, categories]);

  // The gap strips write dex state directly (2026-08-24 ask — the Quick-update
  // choose-then-commit mechanic), but the sync flusher is only bound when the
  // Pokédex page mounts. Bind it here too (idempotent) so a catch recorded
  // from the radar reaches the server backup without a dex visit first.
  //
  // The old DEX_STATE_EVENT dexTick listener (b15b U4: live-drop tiles caught
  // elsewhere) went with the mechanic change: the strip is now FROZEN per
  // event set, the Quick-update card's own rule — a live recompute would
  // delete a just-committed tile and take its undo with it.
  useEffect(() => {
    bindDexSyncApiFetch(apiRequestRaw);
  }, []);

  if (!raids.current && !raids.next) return null;

  // Cross-reference each raid's spawns against the trainer's dex. A present
  // entry in the given variant means it's not a gap. A form-scoped gap (an
  // admin-linked costume) checks the FORM's own row.
  const getPresent = (variant: RaidDexVariant, speciesId: number, formId?: string): boolean =>
    getDexEntryState(variant, speciesId, formId)?.state === "present";

  return (
    <InfoBlock
      icon={Shield}
      title={title}
      trailing={<EvoLinesToggle testId="profile-raid-evo" />}
      data-testid="profile-raid-block"
    >
      <div className="space-y-5">
        {([["Now", raids.current], ["Next", raids.next]] as const)
          .filter(([, ev]) => ev)
          .map(([when, ev]) => (
            <div key={ev!.id}>
              <RaidRowWithGaps
                ev={ev!}
                when={when}
                foci={userPriorities.collectionFoci}
                getPresent={getPresent}
                evoLines={evoLines}
                onOpenEvent={() => {
                  // Land on THIS event's row, not the top of the Calendar.
                  armCalendarEvent(ev!.id);
                  setLocation("/calendar");
                }}
              />
              {/* What this event is worth playing for: feed + curated bonus
                  pills, so the radar says not just WHAT is on but WHY to go.
                  Renders nothing when the event states no bonuses. */}
              <BonusBadges
                bonuses={mergedBonuses(ev!)}
                max={2}
                testId={`radar-bonuses-${ev!.id}`}
              />
              {/* "Remind me" (2026-09-18): the same local notification the
                  transfer bonus card sets, at the event's local start. The
                  button hides itself once the event is on. */}
              <div className="mt-2">
                <EventRemindButton
                  event={{ id: ev!.id, title: ev!.name, start: ev!.start, end: ev!.end, category: ev!.category, spawns: ev!.spawns, bonuses: ev!.bonuses, geoLocked: ev!.geoLocked }}
                  testId={`radar-remind-${ev!.id}`}
                />
              </div>
              {/* The trades strip sits under EACH event, because the leads are
                  per-event: catch what's spawning here to bank a future trade for
                  a friend who still needs it.

                  NOT RENDERED AT ALL when the event has no Pokémon linked to it
                  (a "generic" feed entry nobody has curated yet). The strip is a
                  cross-reference, so with nothing to cross-reference it used to
                  render a heading over the sentence "No Pokémon linked to this
                  event yet" — a block whose entire content was an explanation of
                  why it was empty. The Pro teaser goes too: there is nothing
                  behind it to sell. Reported on device 2026-08-14. */}
              {showFriendTrades && eventHasLinkedSpecies(ev!.spawns ?? []) && (isPro ? (
                <div
                  className="mt-2 rounded-xl p-3 bg-[var(--accent-subtle)] border border-[var(--surface-chip-border)] relative z-10"
                  data-testid={`profile-event-friends-${ev!.id}`}
                >
                  <p className="text-[11px] font-semibold inline-flex items-center gap-1.5 text-[var(--text-primary)]">
                    <Users className="w-3.5 h-3.5 text-[var(--accent-fill)]" /> Friends are looking for:
                  </p>
                  <FriendTradesStrip spawns={ev!.spawns ?? []} />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    openPaywall("profile-priority-widgets", "See which trades your friends still need from this event (Pro)")
                  }
                  data-testid={`profile-event-friends-locked-${ev!.id}`}
                  // Green Pro-gate callout: the neutral surface/border let this
                  // teaser fade into the card, so it now carries the app's accent
                  // green border + faint glow (matching the Quick-update / ProLock
                  // language) so the upsell reads as a gated feature, not chrome.
                  // relative z-10 keeps it above the raid card's drop-shadow glow,
                  // which otherwise bled down over the teaser.
                  className="mt-2 w-full rounded-xl p-3 text-left flex items-center gap-2 relative z-10"
                  style={{
                    background: "var(--accent-fill-glow-faint)",
                    border: "1px solid var(--accent-fill-glow)",
                    boxShadow: "0 0 0 1px var(--accent-fill-glow) inset",
                  }}
                >
                  <Lock
                    className="w-3.5 h-3.5 shrink-0"
                    style={{ color: "var(--accent-fill)" }}
                  />
                  <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                    <span className="font-bold text-[var(--brand-green-on-light)]">Unlock with Pro</span>{" "}
                    to see which trades your friends still need from this event.
                  </span>
                </button>
              ))}
            </div>
          ))}
      </div>
    </InfoBlock>
  );
}

// Cap on how many friends the strip probes for their dex rows. Each pooled
// friend costs one /api/friends/:id/dex fetch on profile load, so this bounds
// the fan-out for a large friends list. The strip only ever shows 3 trades, so
// a modest pool still comfortably fills it; friends beyond the cap don't
// contribute leads until they enter the first slice. Priority friends are moved
// to the front of the list before the cap is applied (useFriendDexes), so a
// pinned friend can never be the one that falls off the end.
const TRADE_FRIEND_POOL_CAP = 12;

// The strip shows THREE trades, drawn from a ranked POOL of up to twelve.
const TRADE_POOL_SIZE = 12;
const TRADE_STRIP_SHOWN = 3;

// "Friends are looking for:" strip (headed "Trades to chase" until 2026-08-07):
// three trade opportunities from THIS event's spawns
// across ALL friends. For each spawning species and each friend, bestFriendTrades
// picks the friend's highest-priority available missing variant
// (Shiny → Lucky → Main) and ranks the best twelve; pickRandomTrades then draws
// three of them, seeded per app launch (LAUNCH_SEED), so the trio is stable this
// session but re-randomises next launch rather than always showing the same top
// three. A trainer sees what to catch here to bank a future trade — and for whom.
//
// Replaces the old "top friend needs" strip, which ranked friends by dex size
// and only ever showed the single most-complete one. The new lead can be any
// friend, which is why each tile names the trainer who needs it (like the wish
// list): the three tiles may be three different friends.
//
// PRIORITY FRIENDS (Pro, Friends > Manage): leads for the trainer's
// pinned friends are computed on their own and placed FIRST, ahead of the random
// draw, so pinning someone guarantees their lead is on screen whenever this
// event can produce one. The remaining slots still draw from the wider pool, so
// pinning never blanks the strip on an event your inner circle has covered.
function FriendTradesStrip({
  spawns,
}: {
  spawns: readonly { speciesId?: number; name?: string; shinyBoost?: boolean; variants?: string[] }[];
}) {
  const { dexes: friendDex, friendCount, isLoadingFriends } =
    useFriendDexes(TRADE_FRIEND_POOL_CAP);

  const trades = useMemo(() => {
    const isAvailable = (variant: FriendGapVariant, id: number) => isVariantAvailable(variant, id);
    // Pinned friends' leads, ranked on their own so they can never be squeezed
    // out of the shared pool by a better-ranked lead for someone else.
    const pinnedDex = friendDex.filter((f) => f.isPriority);
    const pinnedTrades = pinnedDex.length
      ? bestFriendTrades(spawns, pinnedDex, isAvailable, TRADE_STRIP_SHOWN)
      : [];
    const slotsLeft = TRADE_STRIP_SHOWN - pinnedTrades.length;
    if (slotsLeft <= 0) return pinnedTrades.slice(0, TRADE_STRIP_SHOWN);

    const pool = bestFriendTrades(spawns, friendDex, isAvailable, TRADE_POOL_SIZE);
    const taken = new Set(pinnedTrades.map((t) => `${t.friendId}:${t.speciesId}:${t.variant}`));
    const rest = pool.filter((t) => !taken.has(`${t.friendId}:${t.speciesId}:${t.variant}`));
    // Seeded by the app launch so the trio holds steady across re-renders this
    // session and re-shuffles next launch.
    return [...pinnedTrades, ...pickRandomTrades(rest, LAUNCH_SEED, slotsLeft)];
  }, [spawns, friendDex]);

  const pinnedIds = useMemo(
    () => new Set(friendDex.filter((f) => f.isPriority).map((f) => f.friendId)),
    [friendDex],
  );

  // Whether the event has ANY species to cross-reference at all. A "generic"
  // feed event (no spawns) with no admin link yet has none, which is a distinct
  // state from "friends own everything" — see the empty branch below.
  const hasLinkedSpecies = useMemo(
    () => spawns.some((s) => typeof s.speciesId === "number" && s.speciesId >= 1),
    [spawns],
  );

  if (isLoadingFriends) {
    return <p className="text-[10.5px] text-[var(--text-secondary)] mt-1" data-testid="friend-trades-loading">Finding trades…</p>;
  }
  if (friendCount === 0) {
    return (
      <p className="text-[10.5px] text-[var(--text-secondary)] mt-1" data-testid="friend-trades-empty">
        Add friends to see what they're looking for from this event.
      </p>
    );
  }

  return (
    <>
      {trades.length > 0 ? (
        <ul className="mt-2 grid grid-cols-3 gap-2" data-testid="friend-trades">
          {trades.map((t) => (
            <TradeTile
              key={`${t.friendId}:${t.speciesId}:${t.variant}`}
              trade={t}
              isPriority={pinnedIds.has(t.friendId)}
            />
          ))}
        </ul>
      ) : !hasLinkedSpecies ? (
        // No species are attached to this event (a "generic" feed event with no
        // spawns, and no admin link yet). There is nothing to cross-reference,
        // so we must NOT claim friends "already have everything" — that reads as
        // "complete" when the truth is "we don't know what this event features".
        <p className="text-[10.5px] text-[var(--text-secondary)] mt-1" data-testid="friend-trades-unlinked">
          No Pokémon linked to this event yet.
        </p>
      ) : (
        <p className="text-[10.5px] text-[var(--text-secondary)] mt-1" data-testid="friend-trades-none">
          {friendDex.length === 0
            ? "Checking which trades your friends need…"
            : "Your friends already have everything from this event."}
        </p>
      )}
    </>
  );
}

// One trade tile, matching the wish-list format: the friend who needs it on top,
// the species sprite, then the dex-variant badge underneath (so the base dex
// reads "Main", not "Dex") and the species name. A tile for one of the trainer's
// PRIORITY friends carries a filled star beside the name and the accent border,
// so it is obvious why that lead is at the front of the strip.
function TradeTile({ trade, isPriority }: { trade: FriendTrade; isPriority?: boolean }) {
  const variantKey = trade.variant as DexVariantKey;
  const variantLabel = DEX_VARIANT_META[variantKey]?.label ?? trade.variant;
  return (
    <li className="min-w-0">
      <div
        className="rounded-xl p-2 bg-[var(--surface-sunken)] flex flex-col items-center gap-1"
        style={{
          border: isPriority
            ? "1px solid var(--accent-fill-glow)"
            : "1px solid var(--border-subtle)",
        }}
        data-testid={`friend-trade-${trade.friendId}-${trade.speciesId}-${trade.variant}`}
        data-priority-friend={isPriority || undefined}
      >
        <span
          className="w-full truncate text-center text-[9px] font-semibold text-[var(--text-primary)] inline-flex items-center justify-center gap-0.5"
          title={isPriority ? `${trade.friendName} (priority friend)` : trade.friendName}
        >
          {isPriority && (
            <Star
              className="w-2.5 h-2.5 shrink-0"
              style={{ color: "var(--accent-fill)", fill: "var(--accent-fill)" }}
              aria-hidden
            />
          )}
          {trade.friendName}
        </span>
        <CaughtSprite
          id={trade.speciesId}
          size={40}
          shiny={trade.variant === "shiny"}
          ariaLabel={`${trade.friendName} needs ${speciesName(trade.speciesId)} ${variantLabel}`}
        />
        {isDexVariantKey(trade.variant) && <VariantBadge variant={variantKey} />}
        <span
          className="w-full truncate text-center text-[8px] text-[var(--text-tertiary)]"
          title={speciesName(trade.speciesId)}
        >
          {speciesName(trade.speciesId)}
        </span>
      </div>
    </li>
  );
}

// The friends list + each friend's dex rows now come from the shared
// useFriendDexes hook (hooks/useFriendDexes), which owns the per-friend fetch,
// the fan-out cap, and the priority-friends-first ordering, and is shared with
// the Wish List's "who can help" section.

// b13g G2: promote the raid row from a tiny logo thumbnail to a HeroCard with
// the actual raid-boss artwork filling the card width and an orange/raid glow
// border (the `--event-color-raid` token), matching the Calendar hero look.
function RaidRow({
  ev, when, onOpen,
}: { ev: LiveEventApi; when: string; onOpen: () => void }) {
  return (
    <HeroCard
      interactive
      glow="bold"
      glowColor="var(--event-color-raid)"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      data-testid={`profile-raid-row-${ev.id}`}
      className="cursor-pointer"
      media={
        <HeroCardMedia
          src={ev.image}
          alt={ev.name}
          className="h-28"
          fallback={<Shield className="w-7 h-7 text-[var(--event-color-raid)]" />}
        />
      }
    >
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1">
          <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--event-color-raid)]">
            {when}
          </span>
          <span className="block text-[15px] font-bold leading-tight text-[var(--text-primary)]">
            {ev.name}
          </span>
        </span>
        <ChevronRight className="w-4 h-4 text-[var(--text-tertiary)] shrink-0" />
      </div>
    </HeroCard>
  );
}

// b15b U4: a raid row plus the per-variant "still missing" picture-buttons for
// that boss. Each button shows the species sprite (silhouette for the gap) with
// a small variant badge (Dex / Shiny / Lucky / 100%); tapping deep-links to the
// matching variant tab in the Pokédex, pre-filtered to that species.
/** Exported for radarGapQuickAdd.test.tsx — mounted only by EventRadarBlock. */
export function RaidRowWithGaps({
  ev, when, foci, getPresent, onOpenEvent, evoLines = false,
}: {
  ev: LiveEventApi;
  when: string;
  foci: readonly CollectionFocus[];
  getPresent: (variant: RaidDexVariant, speciesId: number) => boolean;
  onOpenEvent: () => void;
  /** Whole evolution lines on the strip (the Evo pill, lib/evoLinesPref). */
  evoLines?: boolean;
}) {
  // The gap strip replicates the Quick-update card's mechanic (2026-08-24 ask):
  // a tap CHOOSES a tile, one button commits the lot, and a committed tile
  // stays in place so tapping it takes just that one back — exact prior state,
  // Main-dex cascade included. The mechanic lives in useGapCommit, shared with
  // EventCatchUp and the Calendar gap strips so the surfaces cannot drift.
  //
  // FROZEN once per event set, the Quick-update rule: an added species stops
  // being a gap, so a live recompute would delete the tile just added and
  // leave nothing to press undo on. Recomputing is the next visit's job.
  // One flat, variant-sorted row (2026-10-12 owner ask, with the Calendar
  // event card): the badge colours do the grouping, not a heading per variant.
  const allGaps = useMemo(
    () =>
      sortGapsByVariant(
        raidDexGaps(evoLines ? expandSpawnsToLines(ev.spawns ?? []) : ev.spawns ?? [], foci, getPresent, {
          // A Max Battle / Max Monday on the radar implies the Dynamax gap for
          // its bosses; no admin D-MAX tick required.
          impliedDynamax: isMaxBattleEventCategory(ev.category, ev.rawType),
        }).filter((g) =>
          // The same gate the Calendar strips and Quick-update apply
          // (eventDexGaps): a NEED tile only for a variant the species / form
          // can carry in GO. The radar skipped it (2026-09-07 owner report:
          // a Shiny Armored Mewtwo tile for a shiny GO never released).
          g.formId
            ? isEventFormAvailable(g.variant, g.formId, g.speciesId)
            : isVariantAvailable(g.variant, g.speciesId),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ev.spawns, foci, ev.category, ev.rawType, evoLines],
  );

  const { tileState, tap, commit, undoAll, selectedCount, addedCount } = useGapCommit(allGaps);

  return (
    <div data-testid={`profile-raid-group-${ev.id}`}>
      <RaidRow ev={ev} when={when} onOpen={onOpenEvent} />
      {allGaps.length > 0 && (
        <div className="mt-2" data-testid={`profile-raid-gaps-${ev.id}`}>
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)] mb-1.5">
            Still missing from your dex · tap to choose
          </p>
          {/* The Quick-update card's own tile and five-across centred wrapping
              row, so idle / chosen / added read identically on both surfaces. */}
          <ul className={GAP_TILE_ROW_CLASS} data-testid={`profile-raid-gap-row-${ev.id}`}>
            {allGaps.map((g) => (
              <li
                key={`${g.variant}:${g.speciesId}:${g.formId ?? ""}`}
                className="min-w-0"
                style={GAP_TILE_CELL_STYLE}
              >
                <CatchUpTile
                  variant={g.variant}
                  speciesId={g.speciesId}
                  formId={g.formId}
                  state={tileState(g)}
                  onToggle={() => tap(g)}
                  testId={`profile-raid-gap-${ev.id}-${g.variant}-${g.speciesId}${g.formId ? `-${g.formId}` : ""}`}
                />
              </li>
            ))}
          </ul>

          {/* The commit. Only ever present when there is something to commit,
              so the strip is inert until the trainer has chosen something. */}
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={commit}
              data-testid={`profile-raid-gaps-commit-${ev.id}`}
              className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
              style={{
                background: "var(--accent-fill)",
                color: "var(--text-on-accent)",
                minHeight: 40,
              }}
            >
              Add {selectedCount} to my Nexus Dex
            </button>
          )}

          {/* After a commit, the way back stays on the card rather than living
              only in a toast that has already gone. */}
          {selectedCount === 0 && addedCount > 0 && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
                {addedCount} added. Tap one to take it back.
              </p>
              <button
                type="button"
                onClick={undoAll}
                data-testid={`profile-raid-gaps-undo-${ev.id}`}
                className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold"
                style={{
                  background: "var(--surface-sunken)",
                  color: "var(--text-secondary)",
                  border: "1px solid var(--border-subtle)",
                  minHeight: 32,
                }}
              >
                Undo all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Season + league tracker. Mounted by Profile directly under the "Raids right
 * now" panel and built from InfoBlock, so its surface, heading and spine dot are
 * the same ones every other block on the profile spine uses.
 *
 * It renders for EVERY trainer. It used to be a child of
 * ProfilePriorityWidgets behind a `playsPvp` questionnaire check, so it silently
 * disappeared for anyone whose primary/secondary style wasn't PvP — the
 * "where did the season tracker go?" report. The season itself is global game
 * state, not a personalisation, so the only personalised part is the league
 * chip row, which still renders only when the trainer picked leagues.
 *
 * THE SEASON COMES FROM THE EVENTS FEED (2026-09-28). It used to come from a
 * hand-appended table (data/pvpSeasons), which nobody appended for Season 28,
 * so the card read "No active season" for three weeks while the feed on the
 * same screen already carried "Twilight Trails". lib/pvpSeasonProgress reads
 * the feed's live `season` row (the one the admin events page adopts, hides
 * or ends early) and falls back to the table only when the feed has none.
 */
export function PvpSeasonPanel() {
  const { userPriorities } = useSession();
  const [, setLocation] = useLocation();
  const leagues = userPriorities.leagueFoci.filter((l) => l !== "none");
  const liveQ = useQuery<LiveEventsResponse>({ queryKey: [eventsLiveKey()] });
  const progress = useMemo(
    () => resolvePvpSeason([...(liveQ.data?.active ?? []), ...(liveQ.data?.upcoming ?? [])], Date.now()),
    [liveQ.data],
  );

  return (
    <InfoBlock icon={Swords} title="PvP season" data-testid="profile-pvp-block">
      {/* The whole card is the way into the Battle Hub (2026-09-10 owner ask):
          the season is the question, the meta is the answer, and until now
          the hub was reachable only from the main menu. A button, not an
          anchor, for the same reason every other Profile link is one: a
          fragment href resets the page scroll. */}
      <button
        type="button"
        onClick={() => setLocation("/battle")}
        aria-label="Open the Battle Hub"
        data-testid="profile-pvp-progress"
        className="w-full text-left rounded-xl p-3 bg-[var(--surface-base)] border border-[var(--border-subtle)] hover-elevate"
      >
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex flex-col">
            <span className="text-[11px] uppercase tracking-wider text-[var(--text-secondary)]">
              Season progress
            </span>
            {progress.season && (
              <span className="text-[12px] font-semibold text-[var(--text-primary)]" data-testid="profile-pvp-season-name">
                {progress.season.name}
                {progress.season.number != null ? ` · Season ${progress.season.number}` : ""}
              </span>
            )}
          </div>
          <span className="text-[11px] text-[var(--text-tertiary)] whitespace-nowrap">
            {progress.season ? `${progress.daysRemaining} days left` : "No active season"}
          </span>
        </div>
        <div className="h-[3px] mt-2 rounded-full overflow-hidden bg-[var(--border-subtle)]">
          <div
            className="h-full bg-[var(--accent-fill)]"
            style={{ width: `${(progress.fraction ?? 0) * 100}%` }}
          />
        </div>
        {progress.season && (
          <div className="mt-1.5 flex items-baseline justify-between text-[10px] text-[var(--text-tertiary)]">
            <span>{progress.daysTotal} days total</span>
            <span>{progress.daysRemaining} days left</span>
          </div>
        )}
        <span
          className="mt-2 inline-flex items-center gap-0.5 text-[10.5px] font-bold uppercase tracking-wider"
          style={{ color: "var(--accent-fill)" }}
          data-testid="profile-pvp-battle-hub-link"
        >
          Battle Hub
          <ChevronRight className="w-3 h-3" aria-hidden />
        </span>
      </button>

      {leagues.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="profile-pvp-leagues">
          {leagues.map((l) => (
            <span
              key={l}
              className="text-[10.5px] font-semibold rounded-full px-2.5 py-1 bg-[var(--surface-chip-bg)] border border-[var(--surface-chip-border)] text-[var(--surface-chip-text)]"
            >
              {leagueFocusLabel(l)}
            </span>
          ))}
        </div>
      )}
    </InfoBlock>
  );
}
